import { defaultDateFormat } from '@macs-frontend/core/utils/consts';
import { DateInput, toDate } from '@macs-frontend/core/utils/date-utils';
import { Company } from '@macs-frontend/features/shared';
import * as fns from 'date-fns/fp';
import { Eq, eqString } from 'fp-ts/Eq';
import { fromCompare } from 'fp-ts/Ord';
import { Brand } from './brand';

export function timeToSec(time?: string | null, containZeroHours = true): number {
  if (!time) {
    return 0;
  }

  const timeSplit = time.split(':');

  if (timeSplit.length === 3) {
    const hours = parseInt(timeSplit[0], 10);
    const minutes = parseInt(timeSplit[1], 10);
    const seconds = parseInt(timeSplit[2], 10);

    return (hours * 60 + minutes) * 60 + seconds;
  }

  if (containZeroHours) {
    const hours = parseInt(timeSplit[0] || '0', 10);
    const minutes = parseInt(timeSplit[1] || '0', 10);

    return (hours * 60 + minutes) * 60;
  }

  const minutes = parseInt(timeSplit[0] || '0', 10);
  const seconds = parseInt(timeSplit[1] || '0', 10);

  return minutes * 60 + seconds;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function secToTime(
  value?: number,
  showSeconds = true,
  showZeroHours = true,
  showZeroMinutes = true,
): string {
  if (!value) {
    return (
      (showZeroHours ? '00' : '') +
      (showZeroHours && (showZeroMinutes || showSeconds) ? ':' : '') +
      (showZeroMinutes ? '00' : '') +
      ((showZeroHours || showZeroMinutes) && showSeconds ? ':' : '') +
      (showSeconds ? '00' : '')
    );
  }

  const seconds = Math.round(value) % 60;
  const minutes = Math.round((value - seconds) / 60) % 60;
  const hours = Math.round((value - seconds - minutes * 60) / 60 / 60);

  const secondsString = zeroPadStart(2)(seconds);
  const minutesString = zeroPadStart(2)(minutes);
  const hoursString = zeroPadStart(2)(hours);

  const showHours = hours !== 0 || showZeroHours;
  const showMinutes = hours !== 0 || showZeroMinutes;

  return (
    (showHours ? hoursString : '') +
    (showHours && (showMinutes || showSeconds) ? ':' : '') +
    (showMinutes ? minutesString : '') +
    ((showHours || showMinutes) && showSeconds ? ':' : '') +
    (showSeconds ? secondsString : '')
  );
}

// HH:mm:ss or HH:mm
export type AppTime = Brand<string, 'AppTime'>;

export function isValidAppTime(
  value?: string | null,
  containZeroHours = true,
): value is AppTime {
  if (!value) {
    return false;
  }

  const timeSplit = value.split(':');

  if (timeSplit.length !== 2 && timeSplit.length !== 3) {
    return false;
  }

  try {
    if (timeSplit.length === 2 && !containZeroHours) {
      // eslint-disable-next-line
      new Date(0, 0, 0, 0, parseInt(timeSplit[0], 10), parseInt(timeSplit[1], 10));
    } else {
      // eslint-disable-next-line
      new Date(
        0,
        0,
        0,
        parseInt(timeSplit[0], 10),
        parseInt(timeSplit[1], 10),
        parseInt(timeSplit[2] || '0', 10),
      );
    }

    return true;
  } catch {
    return false;
  }
}

export function parseAppTime(value?: AppTime | null, containZeroHours = true): number {
  return timeToSec(value, containZeroHours);
}

export function toAppTime(
  value: string | Date | number,
  utcDate = false,
  showSeconds = true,
  containZeroHours = true,
): AppTime {
  if (value instanceof Date) {
    if (utcDate) {
      if (showSeconds) {
        return [value.getUTCHours(), value.getUTCMinutes(), value.getUTCSeconds()]
          .map(zeroPadStart(2))
          .join(':') as AppTime;
      }

      return [value.getUTCHours(), value.getUTCMinutes()]
        .map(zeroPadStart(2))
        .join(':') as AppTime;
    }

    if (showSeconds) {
      return [value.getHours(), value.getMinutes(), value.getSeconds()]
        .map(zeroPadStart(2))
        .join(':') as AppTime;
    }

    return [value.getHours(), value.getMinutes()]
      .map(zeroPadStart(2))
      .join(':') as AppTime;
  }

  if (typeof value === 'number') {
    return secToTime(value, showSeconds, true, true) as AppTime;
  }

  if (!isValidAppTime(value, containZeroHours)) {
    throw new Error('Could not parse app date.');
  }

  const time = parseAppTime(value);

  return secToTime(time, showSeconds, true, true) as AppTime;
}

export const eqAppTime = eqString as Eq<AppTime>;

export function zeroPadStart(maxLength: number): (value: number) => string {
  return (value: number) => value.toString().padStart(maxLength, '0');
}

export const ordAppTime = fromCompare((a?: AppTime, b?: AppTime) => {
  if (a === b) {
    return 0;
  }

  if (!a) {
    return -1;
  }

  if (!b) {
    return 1;
  }

  const aSec = timeToSec(a);
  const bSec = timeToSec(b);

  return aSec > bSec ? 1 : -1;
});

export function formatDate(
  value: DateInput | string,
  companyOrFormat?: Company | string,
) {
  const format = companyOrFormat
    ? typeof companyOrFormat === 'string'
      ? companyOrFormat
      : companyOrFormat?.dateFormat || defaultDateFormat
    : defaultDateFormat;
  const date = toDate(value);

  return fns.format(format, date);
}
